/**
 * BatchCostPreview - Cost calculation display
 */

import { Calculator, Percent, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface BatchCostPreviewProps {
  inputImages: number;
  variantsPerImage: number;
  totalGenerations: number;
  costPerImage: number;
  totalCost: number;
  discountPercent: number;
  isLoading?: boolean;
  error?: string | null;
}

export function BatchCostPreview({
  inputImages,
  variantsPerImage,
  totalGenerations,
  costPerImage,
  totalCost,
  discountPercent,
  isLoading = false,
  error = null,
}: BatchCostPreviewProps) {
  if (error) {
    return (
      <div className="p-4 rounded-lg bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800">
        <div className="text-sm text-red-700 dark:text-red-300">{error}</div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="p-4 rounded-lg bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin text-neutral-500" />
        <span className="text-sm text-neutral-500">Berechne Kosten...</span>
      </div>
    );
  }

  if (inputImages === 0) {
    return (
      <div className="p-4 rounded-lg bg-neutral-100 dark:bg-neutral-800">
        <div className="flex items-center gap-2 text-neutral-500">
          <Calculator className="h-4 w-4" />
          <span className="text-sm">Wahle Bilder um Kosten zu sehen</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 rounded-lg bg-orange-50 dark:bg-orange-950/20 border border-orange-200 dark:border-orange-800">
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <div className="text-sm text-neutral-700 dark:text-neutral-300">
            {inputImages} Bild{inputImages !== 1 ? 'er' : ''} x {variantsPerImage} Variante{variantsPerImage !== 1 ? 'n' : ''} ={' '}
            <span className="font-semibold">{totalGenerations} Generierungen</span>
          </div>

          <div className="flex items-center gap-2 text-xs text-neutral-500 dark:text-neutral-400">
            <span>{costPerImage} Credits pro Bild</span>
            {discountPercent > 0 && (
              <span className={cn(
                'inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium',
                'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
              )}>
                <Percent className="h-3 w-3" />
                {discountPercent}% Batch-Rabatt
              </span>
            )}
          </div>
        </div>

        <div className="text-right">
          <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
            {totalCost} Credits
          </div>
        </div>
      </div>
    </div>
  );
}
